<script setup>
/* eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text */
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import { useAlert } from 'dashboard/composables';
import TalkviaForumAPI from 'dashboard/api/talkviaForum';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Input from 'dashboard/components-next/input/Input.vue';

const props = defineProps({
  files: { type: Array, default: () => [] },
  mentionedUsers: { type: Array, default: () => [] },
  disabled: { type: Boolean, default: false },
});
const emit = defineEmits(['update:files', 'update:mentionedUsers']);

const fileInput = ref(null);
const mentionSearch = ref('');
const mentionOptions = ref([]);
const mentionOpen = ref(false);
const searching = ref(false);
let searchTimer;

const selectedIds = computed(
  () => new Set(props.mentionedUsers.map(user => user.id))
);
const availableOptions = computed(() =>
  mentionOptions.value.filter(user => !selectedIds.value.has(user.id))
);

const searchUsers = async () => {
  searching.value = true;
  try {
    const { data } = await TalkviaForumAPI.mentionableUsers(
      mentionSearch.value
    );
    mentionOptions.value = data;
    mentionOpen.value = true;
  } catch (error) {
    useAlert('Não foi possível buscar os agentes desta conta.');
  } finally {
    searching.value = false;
  }
};

watch(mentionSearch, () => {
  clearTimeout(searchTimer);
  searchTimer = setTimeout(searchUsers, 250);
});
onBeforeUnmount(() => clearTimeout(searchTimer));

const selectFiles = event => {
  const selected = Array.from(event.target.files || []);
  const nextFiles = [...props.files, ...selected];
  if (nextFiles.length > 5) {
    useAlert('Você pode anexar no máximo 5 arquivos.');
    event.target.value = '';
    return;
  }
  const oversized = selected.find(file => file.size > 10 * 1024 * 1024);
  if (oversized) {
    useAlert(`${oversized.name} excede o limite de 10 MB.`);
    event.target.value = '';
    return;
  }
  emit('update:files', nextFiles);
  event.target.value = '';
};

const removeFile = index => {
  emit(
    'update:files',
    props.files.filter((_, fileIndex) => fileIndex !== index)
  );
};

const addMention = user => {
  emit('update:mentionedUsers', [...props.mentionedUsers, user]);
  mentionSearch.value = '';
  mentionOpen.value = false;
};

const removeMention = userId => {
  emit(
    'update:mentionedUsers',
    props.mentionedUsers.filter(user => user.id !== userId)
  );
};

const formatSize = bytes => {
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
};
</script>

<template>
  <div class="space-y-4 rounded-xl border border-n-weak bg-n-alpha-1 p-3">
    <div>
      <div class="mb-2 flex items-center justify-between gap-2">
        <div>
          <p class="text-sm font-medium text-n-slate-12">Anexos</p>
          <p class="text-xs text-n-slate-9">Até 5 arquivos de 10 MB cada.</p>
        </div>
        <button
          type="button"
          class="inline-flex h-8 items-center gap-1.5 rounded-lg border border-n-weak bg-n-solid-1 px-3 text-xs font-medium text-n-slate-11 transition hover:bg-n-alpha-2"
          :disabled="disabled || files.length >= 5"
          @click="fileInput?.click()"
        >
          <Icon icon="i-lucide-paperclip" class="size-4" />
          Adicionar arquivo
        </button>
        <input
          ref="fileInput"
          type="file"
          class="hidden"
          multiple
          accept="image/jpeg,image/png,image/gif,image/webp,application/pdf,text/plain,text/csv,.doc,.docx,.xls,.xlsx"
          :disabled="disabled"
          @change="selectFiles"
        />
      </div>
      <div v-if="files.length" class="flex flex-wrap gap-2">
        <span
          v-for="(file, index) in files"
          :key="`${file.name}-${file.size}-${index}`"
          class="inline-flex max-w-full items-center gap-2 rounded-lg bg-n-solid-1 px-2.5 py-1.5 text-xs text-n-slate-11 outline outline-1 outline-n-weak"
        >
          <Icon icon="i-lucide-file" class="size-4 shrink-0" />
          <span class="max-w-48 truncate">{{ file.name }}</span>
          <span class="text-n-slate-9">{{ formatSize(file.size) }}</span>
          <button
            type="button"
            class="rounded p-0.5 text-n-slate-9 hover:bg-n-alpha-2 hover:text-n-ruby-10"
            :aria-label="`Remover ${file.name}`"
            @click="removeFile(index)"
          >
            <Icon icon="i-lucide-x" class="size-3.5" />
          </button>
        </span>
      </div>
    </div>

    <div class="relative">
      <label class="mb-1 block text-sm font-medium text-n-slate-12">
        Mencionar agentes
      </label>
      <div v-if="mentionedUsers.length" class="mb-2 flex flex-wrap gap-1.5">
        <span
          v-for="user in mentionedUsers"
          :key="user.id"
          class="inline-flex items-center gap-1 rounded-full bg-n-violet-3 px-2.5 py-1 text-xs font-medium text-n-violet-11"
        >
          @{{ user.name }}
          <button
            type="button"
            :aria-label="`Remover menção de ${user.name}`"
            @click="removeMention(user.id)"
          >
            <Icon icon="i-lucide-x" class="size-3" />
          </button>
        </span>
      </div>
      <div class="relative">
        <Input
          v-model="mentionSearch"
          type="search"
          custom-input-class="!pl-10 !bg-n-solid-1"
          placeholder="Buscar agente por nome ou e-mail"
          :disabled="disabled"
          @focus="searchUsers"
          @blur="setTimeout(() => (mentionOpen = false), 150)"
        >
          <template #prefix>
            <Icon
              :icon="searching ? 'i-lucide-loader-2' : 'i-lucide-at-sign'"
              class="pointer-events-none absolute left-3 top-1/2 z-10 size-4 -translate-y-1/2 text-n-slate-9"
              :class="{ 'animate-spin': searching }"
            />
          </template>
        </Input>
      </div>
      <div
        v-if="mentionOpen"
        class="absolute bottom-12 z-50 max-h-[min(13rem,40vh)] w-full overflow-y-auto rounded-lg border border-n-weak bg-n-solid-1 p-1 shadow-2xl"
      >
        <button
          v-for="user in availableOptions"
          :key="user.id"
          type="button"
          class="flex w-full items-center gap-3 rounded-md px-3 py-2 text-left hover:bg-n-alpha-2"
          @mousedown.prevent="addMention(user)"
        >
          <img
            v-if="user.avatar_url"
            :src="user.avatar_url"
            :alt="user.name"
            class="size-8 rounded-full object-cover"
          />
          <span
            v-else
            class="grid size-8 place-items-center rounded-full bg-n-violet-3 text-xs font-semibold text-n-violet-11"
          >
            {{ user.name.charAt(0) }}
          </span>
          <span class="min-w-0">
            <strong class="block truncate text-sm text-n-slate-12">{{
              user.name
            }}</strong>
            <span class="block truncate text-xs text-n-slate-9">{{
              user.email
            }}</span>
          </span>
        </button>
        <p
          v-if="!searching && !availableOptions.length"
          class="px-3 py-4 text-center text-xs text-n-slate-9"
        >
          Nenhum agente encontrado nesta conta.
        </p>
      </div>
    </div>
  </div>
</template>
